// Same margin MUI's Popover keeps from the window edges (marginThreshold).
const WINDOW_MARGIN = 16;
// Below this much room under the field, covering it beats a tiny scrolling menu.
const MIN_MENU_HEIGHT = 120;

const px = (value: string): number => parseFloat(value) || 0;

/**
 * Keeps a dropdown menu under its field instead of on top of it.
 *
 * When a menu opened below its field is taller than the room left under it,
 * MUI v4's Popover shifts it up until it fits in the window, covering the
 * field. Runs after MUI has positioned the paper (Popover `TransitionProps
 * .onEntering`): if the paper was shifted, it is moved back right under the
 * field and capped to the room left there, scrolling the rest. Later
 * repositioning by MUI keeps it there, since the capped paper now fits.
 *
 * MUI records the shift in the vertical transform origin (it adds the shift to
 * it) and leaves the paper's bottom on the window margin, which is how a
 * shifted paper is told apart from one that was meant to overlap its anchor.
 */
export const keepMenuBelowAnchor = (paper: HTMLElement): void => {
  if (!paper.classList.contains('MuiMenu-paper')) return;

  const [originX, originY] = paper.style.transformOrigin.split(' ');
  const shift = px(originY);
  if (shift <= 0) return;

  const windowHeight = window.innerHeight;
  const top = px(paper.style.top);
  const bottom = top + paper.offsetHeight;
  // Not clamped to the window bottom: the offset is by design, leave it.
  if (Math.abs(bottom - (windowHeight - WINDOW_MARGIN)) > 1) return;

  const anchoredTop = top + shift;
  const { marginTop, marginBottom } = window.getComputedStyle(paper);
  const room = windowHeight - WINDOW_MARGIN - anchoredTop - px(marginTop) - px(marginBottom);
  if (room < MIN_MENU_HEIGHT) return;

  paper.style.maxHeight = `${room}px`;
  paper.style.top = `${anchoredTop}px`;
  paper.style.transformOrigin = `${originX} 0px`;
};
